import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/admin/admin-create-team-page.scss';
import { baseURL } from '../../Constants';
import { AuthContext } from '../../context/AuthContext';

// Define interface for Account
interface Account {
  steamID: string;
  name?: string;
  dateOfBirth?: string;
  playerName: string;
  twitter?: string;
  role?: string;
  team?: any;
  isAdmin?: boolean;
  passwordHash?: string;
  age?: number;
}

const AdminCreateTeamPage: React.FC = () => {
  // A better fallback image - using a data URI for a simple placeholder
  const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

  // Fixed timestamp for avatar URLs - only changes when component mounts
  const timeStamp = useState<number>(() => new Date().getTime())[0];

  const navigate = useNavigate();
  const { token, isAdmin } = useContext(AuthContext);
  const [teamName, setTeamName] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [availablePlayers, setAvailablePlayers] = useState<Account[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [season, setSeason] = useState<number>(1);
  const [division, setDivision] = useState<string>('Main');
  const [seed, setSeed] = useState<string>('1');
  const [addToSeason, setAddToSeason] = useState<boolean>(true);

  // Check if user is an admin before allowing access
  useEffect(() => {
    if (!token || !isAdmin) {
      navigate('/admin/login');
    }
  }, [token, isAdmin, navigate]);

  // Fetch available players on component mount
  useEffect(() => {
    fetchAvailablePlayers();

    // Set page title
    document.title = 'Create New Team - Northern Championships Series Admin';
  }, []);

  // Fetch available players (players with no team)
  const fetchAvailablePlayers = async () => {
    setIsLoadingPlayers(true);
    try {
      // Fetch all accounts with authorization header
      const response = await fetch(`${baseURL}/Account`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      
      // Filter to only include accounts without a team
      const availableAccounts = data.filter((account: Account) => !account.team);
      setAvailablePlayers(availableAccounts);
    } catch (err) {
      console.error('Error fetching available players:', err);
      setError('Failed to load available players. Please try again later.');
    } finally {
      setIsLoadingPlayers(false);
    }
  };

  // Handle logo file selection
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check if file is a PNG
      if (file.type !== 'image/png') {
        setError('Only PNG files are accepted for team logos.');
        return;
      }
      
      setLogoFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle player selection/deselection
  const handlePlayerSelection = (steamId: string) => {
    if (selectedPlayers.includes(steamId)) {
      setSelectedPlayers(selectedPlayers.filter(id => id !== steamId));
    } else {
      setSelectedPlayers([...selectedPlayers, steamId]);
    }
  };

  // Create new team
  const handleCreateTeam = async () => {
    
    setError(null);
    
    // Validate inputs
    if (!teamName.trim()) {
      setError('Team name is required');
      return;
    }
    
    if (!logoFile) {
      setError('Team logo is required');
      return;
    }

    
    // Check if admin is authenticated
    if (!token || !isAdmin) {
      setError('You need to be logged in as an admin to perform this action');
      navigate('/admin/login');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // 1. Create the team using the createWithSeason endpoint
      const teamData = {
        id: crypto.randomUUID(),
        name: teamName.trim(),
        logo: null,
        currentDivision: addToSeason ? division : "Main",
        currentSeed: addToSeason ? seed : "1"
      };

      const requestData = {
        team: teamData,
        season: season,
        playerIds: selectedPlayers
      };

      const response = await fetch(`${baseURL}/Team/createWithSeason`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error creating team: ${errorText || `${response.status} ${response.statusText}`}`);
      }

      const createdTeam = await response.json();

      // 2. Team is already added to season during creation if addToSeason was true

      // 3. Upload the logo - make sure we're using the correct team ID
      const formData = new FormData();
      formData.append('file', logoFile);
      
      
      const logoResponse = await fetch(`${baseURL}/Team/${createdTeam.id}/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!logoResponse.ok) {
        const responseText = await logoResponse.text();
        console.error("Logo upload response:", responseText);
        throw new Error(`Error uploading logo: ${logoResponse.status} ${logoResponse.statusText}`);
      }


      // 4. Players are already added during team creation

      // 5. Navigate back to teams list
      navigate('/admin/teams');
      
    } catch (err: any) {
      console.error('Error creating team:', err);
      setError(err.message || 'Failed to create team. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/admin/teams');
  };

  return (
    <div className="admin-create-team-page">
      <div className="header-container">
        <h1>Create New Team</h1>
      </div>
      
      <div className="create-team-panel">
        {error && (
          <div className="error-message">{error}</div>
        )}
        
        <div className="form-container">
          <div className="form-group">
            <label htmlFor="team-name">Team Name <span className="required">*</span></label>
            <input
              id="team-name"
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter team name"
              className="form-input"
              disabled={isLoading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="team-logo">Team Logo <span className="required">*</span></label>
            <div className="logo-container">
              <div className="logo-preview">
                {logoPreview ? (
                  <img src={logoPreview} alt="Team logo preview" />
                ) : (
                  <img src={placeholderImage} alt="No logo selected" />
                )}
              </div>
              
              <div className="logo-upload">
                <label htmlFor="logo-upload" className="upload-btn">
                  {logoPreview ? 'Change Logo' : 'Upload Logo'} <span className="format-hint">(PNG only)</span>
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png"
                  onChange={handleLogoChange}
                  className="file-input"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={addToSeason}
                onChange={(e) => setAddToSeason(e.target.checked)}
                disabled={isLoading}
              />
              Add team to season
            </label>
          </div>

          {addToSeason && (
            <div className="season-fields">
              <div className="form-row" style={{ display: 'flex', gap: '1rem' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="season">Season <span className="required">*</span></label>
                  <input
                    id="season"
                    type="number"
                    value={season}
                    onChange={(e) => setSeason(parseInt(e.target.value) || 1)}
                    min="1"
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="division">Division</label>
                  <input
                    id="division"
                    type="text"
                    value={division}
                    onChange={(e) => setDivision(e.target.value)}
                    placeholder="e.g. Main, Advanced"
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="seed">Seed</label>
                  <input
                    id="seed"
                    type="text"
                    value={seed}
                    onChange={(e) => setSeed(e.target.value)}
                    placeholder="e.g. 1, 2, 3"
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="add-players-search">Add Players (Optional)</label>
            <div className="search-box">
              <input
                id="add-players-search"
                type="text"
                placeholder="Search available players..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                disabled={isLoading}
              />
            </div>
            
            <div className="players-container">
              {isLoadingPlayers ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading players...</p>
                </div>
              ) : availablePlayers.length === 0 ? (
                <div className="empty-state">
                  <p>No available players found.</p>
                </div>
              ) : (
                <div className="players-grid">
                  {availablePlayers
                    .filter(player => 
                      player.playerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (player.name && player.name.toLowerCase().includes(searchTerm.toLowerCase()))
                    )
                    .map(player => {
                      const isSelected = selectedPlayers.includes(player.steamID);
                      return (
                        <div
                          key={player.steamID}
                          className={`player-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => !isLoading && handlePlayerSelection(player.steamID)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') !isLoading && handlePlayerSelection(player.steamID); }}
                        >
                          <div className="player-avatar">
                            <img 
                              src={`${baseURL}/Account/${player.steamID}/avatar?t=${timeStamp}`} 
                              alt={`${player.playerName}'s avatar`}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = placeholderImage;
                              }}
                            />
                          </div>
                          <div className="player-info">
                            <div className="player-name">{player.playerName}</div>
                            {player.name && <div className="player-real-name">{player.name}</div>}
                          </div>
                          <div className="player-selection">
                            <div className="checkbox">
                              {isSelected && <span className="checkmark">✓</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              )}
            </div>
            
            <div className="selected-count">
              {selectedPlayers.length} players selected
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              className="btn btn-cancel"
              onClick={handleCancel}
              disabled={isLoading}
            >
              CANCEL
            </button>
            <button 
              className="btn btn-create"
              onClick={handleCreateTeam}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="btn-spinner"></div>
                  CREATING...
                </>
              ) : (
                'SAVE'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCreateTeamPage;